import { useEffect, useRef } from "react";
import { supabase } from "../../lib/supabase";

// The creative-asset-events Edge Function sends an empty Realtime Broadcast on this topic
// whenever one of the advertiser's variations finishes or fails transcoding. Keep the topic
// and event in step with supabase/functions/creative-asset-events/handler.ts; both sides pin
// them in a test. The ping carries no data: the screen re-reads its creatives through RLS.
export const creativeUpdatesTopic = (advertiserId) => `creative-variations:${advertiserId}`;
export const CREATIVE_UPDATES_EVENT = "changed";

// A batch finishing together (several TTS voices) sends one ping each: re-read once. Also
// bounds the re-read rate if the public topic is spammed.
export const PING_COALESCE_MS = 2_000;
// Backstop while anything is processing: a missed ping, or a variation the cron job completed
// (the cron sends no ping).
export const FALLBACK_REFRESH_MS = 30_000;

// Listens only while something is processing; nothing else can change without the user.
export function useCreativeVariationUpdates(advertiserId, hasProcessing, onChange) {
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    if (!supabase || advertiserId == null || !hasProcessing) return undefined;
    let pending = null;
    const ping = () => {
      if (pending) return;
      pending = setTimeout(() => {
        pending = null;
        onChangeRef.current();
      }, PING_COALESCE_MS);
    };
    const channel = supabase
      .channel(creativeUpdatesTopic(advertiserId))
      .on("broadcast", { event: CREATIVE_UPDATES_EVENT }, ping)
      .subscribe();
    const interval = setInterval(() => {
      if (!document.hidden) onChangeRef.current();
    }, FALLBACK_REFRESH_MS);
    return () => {
      clearTimeout(pending);
      clearInterval(interval);
      supabase.removeChannel(channel);
    };
  }, [advertiserId, hasProcessing]);
}
